import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  Typography,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { DEDUCTION_RUN_COLUMNS } from "../../../Data/Configuration/DeductionApproval";
import { STATUS_CHIP_STYLES } from "../../../styles/CommonStyles";
import { listAllDeductionJobs } from "../../../Services/DriverDeduction.service";

// Map of job status → chip style key
const STATUS_STYLE_MAP = {
  COMPLETED:       "active",
  IN_PROGRESS:     "pending",
  INITIATED:       "pending",
  FAILED:          "inactive",
  PARTIAL_SUCCESS: "pending",
};

/**
 * Deduction Run Tab — shows all payout job runs from the backend.
 * Server-side paginated.
 */
const DeductionRunTab = ({ refreshKey = false }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const fetchInFlightRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(true);
    try {
      const resp = await listAllDeductionJobs({ page, size: rowsPerPage });
      setTableData(resp?.data?.list || []);
      setTotalCount(resp?.data?.count || 0);
    } catch (fetchError) {
      console.error("Failed to fetch deduction jobs", fetchError);
      setTableData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [page, rowsPerPage, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headerRowStyle = {
    "& th": {
      color: "#626262",
      fontWeight: "700",
      fontSize: "1.11vw",
      height: "3.89vw",
      maxHeight: "5vw",
      paddingX: "1vw",
      paddingY: "0.7vw",
      backgroundColor: "#E1F4E4",
    },
  };

  const bodyRowStyle = {
    "&:hover": { backgroundColor: "#EBEBEB" },
    "& td": {
      fontSize: "1vw",
      paddingX: "1vw",
      paddingY: "0.8vw",
      borderBottom: "none",
      color: "#313131",
    },
  };

  if (!loading && tableData.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          backgroundColor: "white",
          minHeight: "13.9vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "0.42vw",
        }}
      >
        <Typography sx={{ color: "black" }}>No deduction run records found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "60vh",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={headerRowStyle}>
              {DEDUCTION_RUN_COLUMNS.map((col) => (
                <TableCell key={col.id} sx={{ width: col.width }}>{col.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={DEDUCTION_RUN_COLUMNS.length} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row) => (
                <TableRow key={row.jobId} sx={bodyRowStyle}>
                  {/* <TableCell>{row.settlementPeriod || "—"}</TableCell> */}
                  <TableCell>{row.initiatedByName || "—"}</TableCell>
                  <TableCell>{row.initiatedAt ? moment(row.initiatedAt).format("DD-MM-YYYY hh:mm A") : "—"}</TableCell>
                  <TableCell>{row.completedAt ? moment(row.completedAt).format("DD-MM-YYYY hh:mm A") : "—"}</TableCell>
                  <TableCell>{row.totalDrivers ?? "—"}</TableCell>
                  <TableCell>{row.processedCount ?? "—"}</TableCell>
                  <TableCell>
                    <Typography sx={{ color: row.failedCount > 0 ? "#D32F2F" : "inherit" }}>
                      {row.failedCount ?? "—"}
                    </Typography>
                  </TableCell>
                  {/* <TableCell>
                    {row.totalAmount != null
                      ? row.totalAmount.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 })
                      : "—"}
                  </TableCell> */}
                  <TableCell>
                    <Chip
                      icon={<FiberManualRecordIcon sx={{ fontSize: "0.6vw !important" }} />}
                      label={row.status}
                      size="small"
                      sx={STATUS_CHIP_STYLES[STATUS_STYLE_MAP[row.status] || "pending"]}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[15, 25, 50]}
        sx={{
          "& .MuiTablePagination-toolbar": {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            overflowX: "hidden",
          },
          "& .MuiTablePagination-select": {
            backgroundColor: "rgba(255,255,255,1)",
            borderRadius: "0.42vw",
            border: "1px solid #D9D9D9",
          },
          "& .MuiTablePagination-actions button": {
            color: "rgba(112,112,112,1)",
            transform: "scale(1.3)",
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            color: "rgba(98,98,98,1)",
            fontSize: "0.97vw",
            textAlign: "center",
            m: "auto",
            letterSpacing: "0.02vw",
          },
        }}
      />
    </Box>
  );
};

export default DeductionRunTab;
